import React, { FormEvent, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  acknowledgeAmendmentInAPI,
  AmendmentAuthorizationBasis,
  AmendmentAcknowledgementEvidence,
  AmendmentEvidence,
  ApiDocument,
  applyAmendmentInAPI,
  getDocumentFromAPI,
  requestAmendmentInAPI,
} from '../services/eCMRService';

const amendmentLoadErrorMessage = 'No se pudo cargar el flujo de enmiendas. Reintenta o contacta soporte.';
const amendmentActionErrorMessage = 'No se pudo completar la acción de enmienda. Reintenta o contacta soporte.';
const amendmentAuthorizationOptions: Array<{
  value: AmendmentAuthorizationBasis;
  label: string;
}> = [
  { value: 'cmr_article8_carrier_check', label: 'CMR Article 8 carrier check' },
  { value: 'cmr_article8_carrier_reservation', label: 'CMR Article 8 carrier reservation' },
  { value: 'cmr_article12_disposal_instruction', label: 'CMR Article 12 disposal instruction' },
  { value: 'cmr_articles14_15_carriage_impediment', label: 'CMR Articles 14-15 carriage impediment' },
  { value: 'cmr_article22_dangerous_goods_notice', label: 'CMR Article 22 dangerous goods notice' },
  { value: 'cmr_articles34_35_successive_carrier_acceptance', label: 'CMR Articles 34-35 successive carrier acceptance' },
];

const AmendmentWorkflows: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const documentId = id || '';
  const [document, setDocument] = useState<ApiDocument | null>(null);
  const [authorizationBasis, setAuthorizationBasis] = useState<AmendmentAuthorizationBasis | ''>('');
  const [reason, setReason] = useState('');
  const [proposedXmlContent, setProposedXmlContent] = useState('');
  const [manualAmendmentReference, setManualAmendmentReference] = useState('');
  const [requestedAmendmentReference, setRequestedAmendmentReference] = useState('');
  const [acknowledgementComment, setAcknowledgementComment] = useState('');
  const [requestedAmendment, setRequestedAmendment] = useState<AmendmentEvidence | null>(null);
  const [acknowledgement, setAcknowledgement] = useState<AmendmentAcknowledgementEvidence | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!documentId) {
      setLoading(false);
      setError('Missing API document id.');
      return;
    }

    const loadDocument = async () => {
      setLoading(true);
      try {
        const loadedDocument = await getDocumentFromAPI(documentId);
        setDocument(loadedDocument);
        setProposedXmlContent(loadedDocument.xmlContent || loadedDocument.content || '');
      } catch (loadError) {
        setError(amendmentLoadErrorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadDocument();
  }, [documentId]);

  const resetFeedback = () => {
    setMessage('');
    setError('');
  };

  const handleRequestAmendment = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    if (!authorizationBasis) {
      setError(amendmentActionErrorMessage);
      return;
    }
    try {
      const amendment = await requestAmendmentInAPI(documentId, {
        reason,
        authorizationBasis,
        proposedChanges: { xmlContent: proposedXmlContent },
      });
      setRequestedAmendment(amendment);
      setRequestedAmendmentReference(amendment.amendmentId);
      setMessage('Amendment requested.');
    } catch (submitError) {
      setError(amendmentActionErrorMessage);
    }
  };

  const handleAcknowledgeAmendment = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    const amendmentReference = manualAmendmentReference || requestedAmendmentReference;
    try {
      const evidence = await acknowledgeAmendmentInAPI(documentId, amendmentReference, {
        acknowledged: true,
        ...(acknowledgementComment ? { comment: acknowledgementComment } : {}),
      });
      setAcknowledgement(evidence);
      setMessage('Amendment acknowledged.');
    } catch (submitError) {
      setError(amendmentActionErrorMessage);
    }
  };

  const handleApplyAmendment = async () => {
    resetFeedback();
    const amendmentReference = manualAmendmentReference || requestedAmendmentReference;
    try {
      const result = await applyAmendmentInAPI(documentId, amendmentReference);
      setRequestedAmendment(result.amendment);
      setDocument(result.document);
      setMessage('Amendment applied.');
    } catch (submitError) {
      setError(amendmentActionErrorMessage);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <p className="text-lg">Loading amendment workflow...</p>
      </div>
    );
  }

  if (!documentId) {
    return (
      <div className="container mx-auto p-4">
        <p className="bg-red-100 border-l-4 border-red-500 p-4 text-red-700">Missing API document id.</p>
      </div>
    );
  }

  const activeAmendmentReferenceAvailable = Boolean(manualAmendmentReference || requestedAmendmentReference);
  const documentVersionEvidenceRecorded = Boolean(
    document?.currentVersion ||
    document?.documentVersionReferencePresent ||
    document?.documentVersionNumber !== undefined
  );

  return (
    <div className="container mx-auto p-4">
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Amendment Workflow</h1>
          <p className="text-sm text-gray-600">
            Partial technical compliance baseline for preserving original version evidence and requesting detectable amendments for API document {documentId}.
          </p>
        </div>
        <Link to="/" className="text-blue-600 hover:text-blue-900 underline">Back to documents</Link>
      </div>

      {document && (
        <section className="mb-4 rounded bg-blue-50 p-4 text-sm text-blue-900">
          <p>Document: {document.title || documentId}</p>
          <p>Status: {document.status}</p>
          <p>Current version evidence: {documentVersionEvidenceRecorded ? 'recorded' : 'not available'}</p>
        </section>
      )}

      {message && <p className="mb-4 rounded border-l-4 border-green-500 bg-green-100 p-4 text-green-700">{message}</p>}
      {error && <p className="mb-4 rounded border-l-4 border-red-500 bg-red-100 p-4 text-red-700">{error}</p>}

      <div className="grid gap-4 lg:grid-cols-2">
        <form onSubmit={handleRequestAmendment} className="rounded bg-white p-4 shadow-md">
          <h2 className="mb-3 text-lg font-bold">Request amendment</h2>
          <p className="mb-3 text-sm text-gray-600">
            Submits proposed amended XML to the backend amendment endpoint. Backend versioning and authorization remain authoritative.
          </p>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="amendmentAuthorizationBasis">
            CMR authorization basis
            <select
              id="amendmentAuthorizationBasis"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={authorizationBasis}
              onChange={(event) => setAuthorizationBasis(event.target.value as AmendmentAuthorizationBasis | '')}
              required
            >
              <option value="">Select authorization basis</option>
              {amendmentAuthorizationOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="amendmentReason">
            Reason
            <textarea
              id="amendmentReason"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              required
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="proposedXmlContent">
            Proposed XML content
            <textarea
              id="proposedXmlContent"
              className="mt-1 block min-h-[10rem] w-full rounded border border-gray-300 p-2 font-mono text-xs"
              value={proposedXmlContent}
              onChange={(event) => setProposedXmlContent(event.target.value)}
              required
            />
          </label>
          <button type="submit" className="rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Request amendment
          </button>

          {requestedAmendment && (
            <div className="mt-4 rounded bg-blue-50 p-3 text-sm text-blue-900">
              <p>Amendment request recorded.</p>
              <p>Parent version evidence: {requestedAmendment.parentVersionReferencePresent || requestedAmendment.parentVersionNumber !== undefined || document?.currentVersion ? 'recorded' : 'not recorded'}</p>
              <p>Authorization evidence: {requestedAmendment.authorizationEvidenceReferencePresent ? 'recorded' : 'not recorded'}</p>
              <p>Exact XML digest: {requestedAmendment.proposedXmlDigestAvailable ? 'recorded' : 'not recorded'}</p>
              <p>Status: {requestedAmendment.status || 'requested'}</p>
            </div>
          )}
        </form>

        <section className="rounded bg-white p-4 shadow-md" aria-labelledby="amendmentReviewHeading">
          <h2 id="amendmentReviewHeading" className="mb-3 text-lg font-bold">Review and apply amendment</h2>
          <p className="mb-3 text-sm text-gray-600">
            Records entitled-party acknowledgement before owner-side apply. This UI is a focused baseline, not a final legal approval matrix.
          </p>
          <form onSubmit={handleAcknowledgeAmendment} className="mb-4">
            <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="amendmentReference">
              Amendment reference
              <input
                id="amendmentReference"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={manualAmendmentReference}
                onChange={(event) => setManualAmendmentReference(event.target.value)}
                required={!requestedAmendmentReference}
              />
            </label>
            <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="acknowledgementComment">
              Acknowledgement comment
              <textarea
                id="acknowledgementComment"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={acknowledgementComment}
                onChange={(event) => setAcknowledgementComment(event.target.value)}
              />
            </label>
            <button type="submit" className="rounded bg-green-600 px-4 py-2 font-bold text-white hover:bg-green-700">
              Acknowledge amendment
            </button>
          </form>

          <button
            type="button"
            onClick={handleApplyAmendment}
            disabled={!activeAmendmentReferenceAvailable}
            className="rounded bg-purple-600 px-4 py-2 font-bold text-white hover:bg-purple-700 disabled:opacity-50"
          >
            Apply amendment
          </button>

          {acknowledgement && (
            <div className="mt-4 rounded bg-green-50 p-3 text-sm text-green-900">
              <p>Acknowledgement evidence: recorded</p>
              <p>Decision: {acknowledgement.acknowledged ? 'acknowledged' : 'rejected'}</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AmendmentWorkflows;
